import { FC, Props } from "./typings";
import styles from "./styles.module.scss";
import { useNavigate } from "react-router-dom";
import { useContext, useRef, useState } from "react";
import classNames from "classnames";
import DossierFilesContext from "contexts/DossierFilesContext";
import dossierService from "api/dossierService";
import TemplateActiveModal from "components/TemplateActiveModal";
import DeskIcon from "assets/icons/book.svg?react";
import DraftIcon from "assets/icons/draft.svg?react";
import SignatureIcon from "assets/icons/signature.svg?react";
import HistoryIcon from "assets/icons/history.svg?react";
import ShareIcon from "assets/icons/share.svg?react";
import CaseIcon from "assets/icons/case.svg?react";
import DeskActiveModal from "components/DeskActiveModal";
import Popper from "components/Popper";
import HistoryModal from "components/HistoryModal";
import { Tooltip } from "@mui/material";
import ChatIcon from "assets/icons/chat_bubble_outline.svg?react";
import CommentsModal from "components/CommentsModal";
import { IPackage } from "dto/IPackage";
import { useTranslation } from "react-i18next";

const IconsCell: FC<Props> = ({ element, caseId, isDesk }) => {
  const { t } = useTranslation();
  const wrapperRef = useRef();
  const { dossierFiles, setDossierFiles } = useContext(DossierFilesContext);
  const isSigned =
    !!element?.SignedPdfUri ||
    !!element?.SignedXadesUri ||
    (element?.FileName ?? "").toLowerCase().includes("sig");
  const [showDesk, setShowDesk] = useState(false);
  const [showTemplate, setShowTemplate] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const { mutate: setTemplate } = dossierService.useSetTemplate();
  const { mutate: setDesk } = dossierService.useSetDesk();
  const navigate = useNavigate();

  const navigateToCase = () => {
    navigate(`/cases/${element.CaseId}/dossier`);
  };

  const handleSetDesk = () => {
    setShowDesk(false);
    setDesk(
      {
        Items: [element.DossierId],
        Show: !element.DeskInfo_IsOn,
      },
      {
        onSuccess: () => {
          const files = [...dossierFiles];

          if (isDesk) {
            setDossierFiles(
              files.filter(
                (x) => x.Id !== element.Id && x.Kind !== element.Kind
              )
            );
          } else {
            const el = files.find(
              (c) => c.Id === element.Id && c.Kind === element.Kind
            );

            if (el) {
              el.DeskInfo_IsOn = !el.DeskInfo_IsOn;
            }

            setDossierFiles(files);
          }
        },
      }
    );
  };

  const handleSetAsTemplate = () => {
    console.log("Handle set as template");
    setShowTemplate(false);
    setTemplate(
      {
        Items: [element.DossierId],
        IsTemplate: !element.IsTemplate,
      },
      {
        onSuccess: () => {
          const files = [...dossierFiles];

          const el = files.find(
            (c) => c.Id === element.Id && c.Kind === element.Kind
          );

          if (el) {
            el.IsTemplate = !el.IsTemplate;
          }

          setDossierFiles(files);
        },
      }
    );
  };

  return (
    <div className={styles.icons} ref={wrapperRef}>
      <Tooltip
        title="Szablon"
        className={element.IsTemplate ? styles.visible : styles.collapsed}
      >
        <div>
          <DraftIcon
            className={classNames(styles.draftIcon)}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowTemplate(!showTemplate);
            }}
          />
        </div>
      </Tooltip>

      <Tooltip
        title="Komentarze"
        className={
          element.CommentsCount > 0 ? styles.visible : styles.collapsed
        }
      >
        <div>
          <ChatIcon
            className={classNames(styles.draftIcon)}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowComments(!showComments);
            }}
          />
        </div>
      </Tooltip>

      {!element.DeskInfo_IsOn && (
        <DeskIcon
          className={classNames(
            styles.deskIcon,
            element.DeskInfo_IsOn ? styles.visible : styles.collapsed
          )}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowDesk(!showDesk);
          }}
        />
      )}

      {element.DeskInfo_IsOn && (
        <DeskIcon
          className={classNames(
            styles.deskIcon,
            element.DeskInfo_IsOn ? styles.visible : styles.collapsed
          )}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowDesk(!showDesk);
          }}
        />
      )}
      {showDesk && (
        <Popper
          anchorEl={wrapperRef?.current}
          open={showDesk}
          setOpen={setShowDesk}
        >
          <DeskActiveModal
            onRemove={handleSetDesk}
            toggle={() => {
              setShowDesk(false);
            }}
          />
        </Popper>
      )}
      <Tooltip
        title="Wersje"
        className={
          (element.VersionsCount ?? 0) > 0 && !isSigned
            ? styles.visible
            : styles.collapsed
        }
      >
        <div>
          <HistoryIcon
            className={classNames(styles.icn)}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowHistory(!showHistory);
            }}
          />
        </div>
      </Tooltip>
      <Tooltip
        title="Podpisano"
        className={
          ((element.VersionsCount ?? 0) > 0 ||
            (element.FileName ?? "").toLowerCase().includes("sig")) &&
          isSigned
            ? styles.visible
            : styles.collapsed
        }
      >
        <div>
          <SignatureIcon
            className={classNames(styles.signatureIcon)}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowHistory(!showHistory);
            }}
          />
        </div>
      </Tooltip>
      <Tooltip
        title={t("statuses.shared")}
        className={element.IsShared ? styles.visible : styles.collapsed}
      >
        <div>
          <ShareIcon
            className={classNames(styles.shareIcon)}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowShare(!showShare);
            }}
          />
        </div>
      </Tooltip>
      <Tooltip
        title={t("cases.goToCase")}
        className={
          !caseId && element.CaseId ? styles.visible : styles.collapsed
        }
      >
        <div>
          <CaseIcon
            className={classNames(styles.shareIcon)}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              navigateToCase();
            }}
          />
        </div>
      </Tooltip>

      {showHistory && (
        <Popper
          anchorEl={wrapperRef?.current}
          open={showHistory}
          setOpen={setShowHistory}
        >
          <HistoryModal
            pack={element as any}
            toggle={() => setShowHistory(false)}
          />
          {/* <></> */}
        </Popper>
      )}

      <Popper
        anchorEl={wrapperRef?.current}
        open={showTemplate}
        setOpen={setShowTemplate}
      >
        <TemplateActiveModal
          onRemove={handleSetAsTemplate}
          toggle={() => {
            setShowTemplate(false);
          }}
        />
      </Popper>

      <Popper
        anchorEl={wrapperRef?.current}
        open={showComments}
        setOpen={setShowComments}
      >
        <CommentsModal
          pack={
            {
              Id: element.AttachmentParentId ?? element.Id,
              Kind: element.Kind,
              Attachments: [
                {
                  Id: element.Id,
                  FileName: element.FileName,
                  Label: element.FileName,
                  Uri: element.FileUri,
                  CommentsCount: element.CommentsCount,
                },
              ],
            } as IPackage
          }
          attachmentId={element.FileUri ?? element.Id}
          toggle={setShowComments}
        ></CommentsModal>
      </Popper>
    </div>
  );
};

export default IconsCell;
